import { TSessionDetails } from '@api/io/session.io';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { useSessionThumbnail } from '@hooks/useImageUrl';
import { HeroContainer } from '@fe-learner-common/components/HeroContainer';
import { Breadcrumb } from '@fe-learner-common/components/Breadcrumb';
import { FaIcon, Tag } from '@fe-learner-common';
import { InstructorsPromo } from '@fe-learner-common/components/InstructorsPromo';
import { Tabs } from '@fe-learner-common/components/Tabs';
import { TTabKeys } from '@routes/session-details/SessionDetailsRoute';
import { useApplyPageConfig } from '@routes/useApplyPageConfig';
import { useEnrollmentActions } from '@routes/session-details/hooks/useEnrollmentActions';
import { useSessionDetailsTags } from '@routes/session-details/hooks/useSessionDetailsTags';
import { tabMap } from '@utils/tabMap';

export const SessionDetailsPage = ({
  tabKeys,
  sessionDetails,
}: {
  tabKeys: TTabKeys[];
  sessionDetails: TSessionDetails;
}) => {
  useApplyPageConfig();
  const intl = useIntl();
  const params = useParams();
  const selectedTab = params['*'];

  const sessionImage = useSessionThumbnail({
    session: sessionDetails,
    size: 640,
  });
  const tabs = tabKeys.map((key) => tabMap[key]);

  const enrollmentActions = useEnrollmentActions({ sessionDetails });
  const tags = useSessionDetailsTags(sessionDetails);

  return (
    <div>
      <HeroContainer
        courseImage={sessionImage}
        courseImageAlt={intl.formatMessage({
          id: 'learner.common.course.image',
        })}
        topSlot={
          <Breadcrumb
            rootTitle={intl.formatMessage({ id: 'catalog.directory.title' })}
            items={[
              {
                label: sessionDetails.name || '',
                href: `/session/${sessionDetails.id}`,
              },
            ]}
          />
        }
        background={{
          backdrop: sessionImage,
          image: sessionDetails?.bgImage,
          color: sessionDetails?.bgColor,
        }}
      >
        <div>
          <h1 className="text-headline-sm">{sessionDetails?.name}</h1>
          <div className="flex flex-wrap gap-3 pt-2">
            {tags
              ? Object.entries(tags).map(([key, tag]) =>
                  tag ? (
                    <Tag
                      key={key}
                      type={tag.type}
                      className="whitespace-nowrap"
                    >
                      <FaIcon width={12} icon={tag.icon} />
                      {tag.text}
                    </Tag>
                  ) : null,
                )
              : null}
          </div>
        </div>
        <div className="flex flex-col gap-5 lg:flex-row lg:flex-nowrap">
          <div className="flex shrink-0 flex-wrap gap-5 [&>*]:flex-1">
            {enrollmentActions?.enrollmentAction}
            {enrollmentActions?.disenrollmentAction}
          </div>

          {!!enrollmentActions?.enrollmentAction &&
          !!enrollmentActions?.disenrollmentAction &&
          sessionDetails.trainers?.length ? (
            <div className="hidden border-r-2 border-neutral-300 lg:block" />
          ) : null}

          {sessionDetails.trainers?.length ? (
            <Link to="./instructors">
              <InstructorsPromo
                visiblePersons={sessionDetails.trainers
                  .slice(0, 3)
                  .map((trainer) => ({
                    name: trainer.name || '',
                    thumbnail: trainer.thumbnail || '',
                  }))}
                totalPersons={sessionDetails.trainers?.length}
              />
            </Link>
          ) : null}
        </div>
      </HeroContainer>
      <Tabs tabs={tabs} selectedTab={selectedTab} />
    </div>
  );
};
